import React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';
import grey from '@material-ui/core/colors/grey';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import { loadJobUsers } from '../../action/job/load-job-users';
import { loadNotificationTypes } from '../../action/job/load-notification-types';
import { populateNotificationSettingsFromOrg } from '../../action/job/populate-notification-settings-from-org';
import { updateOrgDefaultNotificationSettings } from '../../action/org/update-org-default-notification-settings';
import { createApplicationStatusSubscription } from '../../application-status-subscription/actions/create-application-status-subscription';
import { deleteApplicationStatusSubscription } from '../../application-status-subscription/actions/delete-application-status-subscription';
import { loadApplicationStatusSubscriptions } from '../../application-status-subscription/actions/load-application-status-subscriptions';
import { updateManyApplicationStatusSubscription } from '../../application-status-subscription/actions/update-many-application-status-subscription';
import {
  applicationStatusSubscriptionsStateForJobIdSelector,
  applicationStatusSubscriptionsSubscriberMapForJobIdSelector,
} from '../../application-status-subscription/application-status-subscription-selector';
import { userSelector } from '../../reducers/current-user-selectors';
import { usersForJobIdSelector } from '../../reducers/job-selectors';
import { orgSelector } from '../../reducers/org-selectors';

import { UiButton } from '../../ui/ui-button';

import { StatusChangeChips } from './status-change-chips';
import { StatusChangeNotificationSelect } from './status-change-notification-select';

import { AddCustomEmailButton } from '../../application-status-subscription/widgets/add-custom-email-button';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import {
  NOTIFICATION_TYPES,
  NotificationType,
  NotificationTypeName,
} from '../../util/notification-types';
import { LoadingMessage } from '../../widgets/loading-message';

const styles = () =>
  createStyles({
    button: {
      minHeight: '0px',
      paddingBottom: '2px',
      paddingTop: '2px',
      marginTop: '5px',
      float: 'left',
      fontSize: '0.7rem',
      color: grey[500],
    },
  });

const mapStateToProps = (state: any, { jobId }: InputProps) => {
  const job = state.jobs.jobsById[jobId] as Job;
  return {
    job,
    org: orgSelector(state, job.orgId),
    notificationTypes: state.jobs.notificationTypes as NotificationType[],
    users: usersForJobIdSelector(state, jobId),
    currentUser: userSelector(state),
    applicationStatusSubscriptionsSubscriberMap:
      applicationStatusSubscriptionsSubscriberMapForJobIdSelector(state, jobId),
    applicationStatusSubscriptionsState: applicationStatusSubscriptionsStateForJobIdSelector(
      state,
      jobId,
    ),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { jobId }: InputProps,
) => ({
  actions: {
    loadNotificationTypes: () => dispatch(loadNotificationTypes()),
    loadUsers: () => dispatch(loadJobUsers(jobId)),
    updateOrgDefaultNotificationSettings: (orgId: number) =>
      dispatch(updateOrgDefaultNotificationSettings(orgId, jobId)),
    populateNotificationSettingsFromOrg: () => dispatch(populateNotificationSettingsFromOrg(jobId)),
    loadApplicationStatusSubscriptions: () =>
      dispatch(loadApplicationStatusSubscriptions({ jobId })),
    createApplicationStatusSubscription: (
      createRequest: ApplicationStatusSubscriptionCreateRequest,
    ) => dispatch(createApplicationStatusSubscription(createRequest)),
    deleteApplicationStatusSubscription: (applicationStatusSubscriptionId: number) =>
      dispatch(deleteApplicationStatusSubscription(applicationStatusSubscriptionId)),
    updateManyStatusChangeSubscriptions: ({
      emailSubscriptions,
      usernameSubscriptions,
    }: {
      emailSubscriptions: { [email: string]: Array<ApplicationStatus> };
      usernameSubscriptions: { [username: string]: Array<ApplicationStatus> };
    }) =>
      dispatch(
        updateManyApplicationStatusSubscription({
          jobId,
          emailSubscriptions,
          usernameSubscriptions,
        }),
      ),
  },
});

type TwoDimensionalMap = { [username: string]: { [notificationType: string]: boolean } };

interface InputProps {
  jobId: number;
  isSubmitting: boolean;
  isEditing?: boolean;
  showOnlyRequiredNotificationTypes?: boolean;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
interface Props
  extends InputProps,
    StateProps,
    DispatchProps,
    ReduxFormFieldProps<Array<NotificationSetting>>,
    WithStyles<typeof styles> {}

type State = {
  statusChangeNotificationUsernames: { [username: string]: Array<ApplicationStatus> };
  statusChangeNotificationEmails: { [email: string]: Array<ApplicationStatus> };
};

const sortByAdminsThenName = (a: User, b: User) => {
  if (a.isAdmin && !b.isAdmin) {
    return 1;
  }
  if (b.isAdmin && !a.isAdmin) {
    return -1;
  }
  if (a.firstName < b.firstName) {
    return -1;
  }
  if (a.firstName > b.firstName) {
    return 1;
  }

  return 0;
};

const orderedJobNotificationEnums = [
  'DAILY_DIGEST',
  'INCOMING_TEXT_NOTIFICATION',
  'APPLICANT_REACH_OUT',
];

// Ideally, this component would largely be the same as the ApplicantStatusSubscriptionsTable component
// used for Org level settings, but that relies on us removing the job redux form first
class JobNotificationSettingsComponent extends React.PureComponent<Props, State> {
  state: State = {
    statusChangeNotificationUsernames: {},
    statusChangeNotificationEmails: {},
  };

  componentDidMount() {
    const { notificationTypes, actions } = this.props;

    if (!notificationTypes) {
      actions.loadNotificationTypes();
    }

    actions.loadUsers();
    actions.loadApplicationStatusSubscriptions().then(() => {
      this.setState({
        statusChangeNotificationUsernames:
          this.props.applicationStatusSubscriptionsSubscriberMap.users,
        statusChangeNotificationEmails:
          this.props.applicationStatusSubscriptionsSubscriberMap.customEmails,
      });
    });
  }

  componentDidUpdate(prevProps: Props) {
    const { actions, applicationStatusSubscriptionsSubscriberMap, isEditing, isSubmitting } =
      this.props;

    actions.loadUsers();
    actions.loadApplicationStatusSubscriptions();

    // PROD-2604: For speed, we sacrificed some scalability for the applicationStatusSubscription
    // feature by hacking it into the redux form in this component
    // This is a bit fragile and we'll need to make sure we don't break these
    // checkboxes when editing this component.
    // See suggestions for fixes in the description of that ticket.
    if (
      applicationStatusSubscriptionsSubscriberMap &&
      (prevProps.applicationStatusSubscriptionsSubscriberMap !==
        applicationStatusSubscriptionsSubscriberMap ||
        prevProps.isEditing !== isEditing)
    ) {
      this.setState({
        statusChangeNotificationUsernames: applicationStatusSubscriptionsSubscriberMap.users,
        statusChangeNotificationEmails: applicationStatusSubscriptionsSubscriberMap.customEmails,
      });
    }

    // instead of using the submit of redux-form, we listen for that event
    // and fire off a separate started status subscriptions "submit"
    if (isSubmitting && prevProps.isSubmitting !== isSubmitting) {
      this.submitStatusChangeSubscriptions();
    }
  }

  useDefaults = () => {
    const { actions } = this.props;
    actions.populateNotificationSettingsFromOrg();
  };

  updateDefaults = () => {
    const { job, actions } = this.props;
    if (!job) {
      return;
    }

    actions.updateOrgDefaultNotificationSettings(job.orgId);
  };

  submitStatusChangeSubscriptions = () => {
    const { actions } = this.props;
    const {
      statusChangeNotificationUsernames: usernameSubscriptions,
      statusChangeNotificationEmails: emailSubscriptions,
    } = this.state;
    actions.updateManyStatusChangeSubscriptions({ usernameSubscriptions, emailSubscriptions });
  };

  addCustomEmailSubscription = (email: string) => {
    this.setState((prevState) => ({
      statusChangeNotificationEmails: {
        ...prevState.statusChangeNotificationEmails,
        [email]: [],
      },
    }));
  };

  handleSelectStatusSubscription =
    ({ username, email }: { username?: string; email?: string }) =>
    (event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const addedStatus = event.target.value as ApplicationStatus;

      if (username) {
        this.setState((prevState) => ({
          statusChangeNotificationUsernames: {
            ...prevState.statusChangeNotificationUsernames,
            [username]: (prevState.statusChangeNotificationUsernames[username] || []).concat([
              addedStatus,
            ]),
          },
        }));
      } else if (email) {
        this.setState((prevState) => ({
          statusChangeNotificationEmails: {
            ...prevState.statusChangeNotificationEmails,
            [email]: (prevState.statusChangeNotificationEmails[email] || []).concat([addedStatus]),
          },
        }));
      }
    };

  handleRemoveStatusSubscription =
    ({ username, email }: { username?: string; email?: string }) =>
    (removedStatus: ApplicationStatus) =>
    () => {
      if (username) {
        this.setState((prevState) => ({
          statusChangeNotificationUsernames: {
            ...prevState.statusChangeNotificationUsernames,
            [username]: (prevState.statusChangeNotificationUsernames[username] || []).filter(
              (status) => status !== removedStatus,
            ),
          },
        }));
      } else if (email) {
        this.setState((prevState) => ({
          statusChangeNotificationEmails: {
            ...prevState.statusChangeNotificationEmails,
            [email]: (prevState.statusChangeNotificationEmails[email] || []).filter(
              (status) => status !== removedStatus,
            ),
          },
        }));
      }
    };

  updateSubscription =
    (username: string, notificationType: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      const { input } = this.props;
      let subscriptions = input.value;

      // When this input is empty, it'll return an empty string
      // @ts-ignore
      if (subscriptions === '') {
        subscriptions = [];
      }

      const newSettings: Array<Partial<NotificationSetting>> = subscriptions.slice();
      const index = newSettings.findIndex(
        (s) => s.username === username && s.notificationType === notificationType,
      );
      if (index > -1) {
        const newItem = { ...newSettings[index], isOn: checked };
        newSettings.splice(index, 1, newItem);
      } else {
        newSettings.push({
          username,
          notificationType,
          isOn: checked,
        });
      }

      // This is pretty hard to type. The new settings array technically has
      // both a new setting request (which is what this method adds), and
      // the existing settings. There should probably be a new type which is
      // { username, notificationType, isOn } which is all this component cares
      // about
      input.onChange(newSettings as Array<NotificationSetting>);
    };

  render() {
    const {
      org,
      notificationTypes,
      users,
      classes,
      isEditing,
      showOnlyRequiredNotificationTypes,
      input,
      currentUser,
      applicationStatusSubscriptionsSubscriberMap,
    } = this.props;
    if (!users || !notificationTypes) {
      return <LoadingMessage />;
    }

    const usesPhoneInterview = org?.usesPhoneInterview || false;

    let subscriptions = input.value;

    // When this input is empty, it'll return an empty string
    // @ts-ignore
    if (subscriptions === '') {
      subscriptions = [];
    }

    const requiredNotificationTypes: NotificationTypeName[] = [
      NOTIFICATION_TYPES.INCOMING_TEXT_NOTIFICATION,
    ];
    // Filter to notificationTypes that are relevant to JobIds
    const filteredNotificationTypes = notificationTypes
      .filter(
        (notificationType: NotificationType) =>
          orderedJobNotificationEnums.find((type) => type === notificationType.type) &&
          (!showOnlyRequiredNotificationTypes ||
            requiredNotificationTypes.includes(notificationType.type)),
      )
      .sort(
        (a: NotificationType, b: NotificationType) =>
          orderedJobNotificationEnums.indexOf(a.type) - orderedJobNotificationEnums.indexOf(b.type),
      );

    // Figure out which options are checked by username and notification type
    const isCheckedMap = subscriptions.reduce(
      (
        isEnabledByUserNotificationType: TwoDimensionalMap,
        notificationSetting: NotificationSetting,
      ) => {
        if (isEnabledByUserNotificationType[notificationSetting.username]) {
          isEnabledByUserNotificationType[notificationSetting.username][
            notificationSetting.notificationType
          ] = typeof notificationSetting.isOn === 'undefined' || notificationSetting.isOn === true;
        } else {
          isEnabledByUserNotificationType[notificationSetting.username] = {
            [notificationSetting.notificationType]:
              typeof notificationSetting.isOn === 'undefined' || notificationSetting.isOn === true,
          };
        }
        return isEnabledByUserNotificationType;
      },
      {},
    );

    // IF (02/23/21): We only want to show an admin user in the list if they're the user currently
    // signed in and viewing it.
    const displayUsers = users
      .filter((u) => !u.isAdmin || u.username === currentUser.username)
      .sort(sortByAdminsThenName);

    // Fill in the rest of the users and notification types as false
    users.forEach((u) => {
      const { username } = u;
      if (!isCheckedMap[username]) {
        isCheckedMap[username] = {};
      }
      filteredNotificationTypes.forEach((notificationType: NotificationType) => {
        if (!isCheckedMap[username][notificationType.type]) {
          isCheckedMap[username][notificationType.type] = false;
        }
      });
    });

    const cellWidth = `${Math.round(100 / filteredNotificationTypes.length)}%`;

    const { customEmails: applicationStatusSubscriptionEmailsMap } =
      applicationStatusSubscriptionsSubscriberMap;

    // Unlike usernames, emails need to come out of state to handle adding new
    // emails to the table before they're fully submitted
    const { statusChangeNotificationEmails } = this.state;
    const displayCustomEmails = Array.from(
      new Set([
        ...Object.keys(applicationStatusSubscriptionEmailsMap),
        ...Object.keys(statusChangeNotificationEmails),
      ]),
    );

    const applicationStatusSubscriptionLabel = 'Applicant status changes';

    // This is the total number of columns in the table, so we can easily span a full row
    const colCount = 5;

    return (
      <>
        {!isEditing ? (
          <Grid container item alignItems="center" xs={12} className="u-margin-bottom">
            <Grid item>
              <UiButton variant="dismiss" onClick={this.useDefaults} className={classes.button}>
                Use Organization Defaults
              </UiButton>
            </Grid>
            <Grid item>
              <UiButton variant="dismiss" onClick={this.updateDefaults} className={classes.button}>
                Set as Organization Defaults
              </UiButton>
            </Grid>
            <Grid item>
              <Tooltip
                title="Use or override default notification settings for daily job summary, incoming text messages, and saved applicant reminders"
                placement="right"
              >
                <span>
                  <MaterialDesignIcon
                    size="sm"
                    iconName={ICON_NAMES.HELP}
                    className="u-margin-left u-margin-top"
                  />
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        ) : null}
        <Hidden xsDown>
          <Table stickyHeader style={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: cellWidth }}>User</TableCell>
                {filteredNotificationTypes.map((t: NotificationType) => (
                  <TableCell
                    key={`notification-type-${t.type}`}
                    style={{ width: cellWidth }}
                    align="center"
                  >
                    {t.description}
                  </TableCell>
                ))}
                {!showOnlyRequiredNotificationTypes ? (
                  <TableCell style={{ width: cellWidth }} align="center">
                    {applicationStatusSubscriptionLabel}
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayUsers.map((u) => (
                <TableRow key={`user-notifications-${u.username}`}>
                  <TableCell>
                    {u.firstName} {u.lastName}
                    {u.isAdmin && <Typography variant="caption">(admin)</Typography>}
                  </TableCell>
                  {filteredNotificationTypes.map((t: NotificationType) => (
                    <TableCell key={`user-${u.username}-type-${t.type}`} align="center">
                      {isEditing ? (
                        <Checkbox
                          onChange={this.updateSubscription(u.username, t.type)}
                          checked={isCheckedMap[u.username][t.type]}
                        />
                      ) : (
                        <Typography variant="body2">
                          {isCheckedMap[u.username][t.type] ? (
                            <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                          ) : null}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                  {!showOnlyRequiredNotificationTypes ? (
                    <TableCell align="center">
                      {isEditing ? (
                        <StatusChangeNotificationSelect
                          emailOrUsername={u.username}
                          subscribedStatuses={
                            this.state.statusChangeNotificationUsernames[u.username]
                          }
                          usesPhoneInterview={usesPhoneInterview}
                          onAdd={this.handleSelectStatusSubscription({ username: u.username })}
                          onRemove={this.handleRemoveStatusSubscription({ username: u.username })}
                        />
                      ) : (
                        <StatusChangeChips
                          keyPrefix={u.username}
                          selectedStatuses={
                            this.state.statusChangeNotificationUsernames[u.username]
                          }
                        />
                      )}
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
              {/* PROD-2606 : Add this functionality to the hidden mobile view below */}
              {!showOnlyRequiredNotificationTypes ? (
                <>
                  {displayCustomEmails.map((email) => (
                    <TableRow key={email}>
                      {/* Emails don't wrap by default like names do so we break on the @ sign with word-break */}
                      <TableCell style={{ wordBreak: 'break-word' }}>{email}</TableCell>
                      {filteredNotificationTypes.map(({ type }) => (
                        <TableCell key={`user-${email}-type-${type}`} align="center">
                          {isEditing && <Checkbox disabled indeterminate />}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        {isEditing ? (
                          <StatusChangeNotificationSelect
                            emailOrUsername={email}
                            subscribedStatuses={this.state.statusChangeNotificationEmails[email]}
                            usesPhoneInterview={usesPhoneInterview}
                            onAdd={this.handleSelectStatusSubscription({ email })}
                            onRemove={this.handleRemoveStatusSubscription({ email })}
                          />
                        ) : (
                          <StatusChangeChips
                            keyPrefix={email}
                            selectedStatuses={this.state.statusChangeNotificationEmails[email]}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {isEditing && (
                    <TableRow>
                      <TableCell colSpan={colCount}>
                        <AddCustomEmailButton onSubmit={this.addCustomEmailSubscription} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : null}
            </TableBody>
          </Table>
        </Hidden>
        <Hidden smUp>
          <hr />
          {displayUsers.map(({ username, firstName, lastName, isAdmin }) => (
            <Grid item xs={12} key={`user-notifications-${username}`}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  {firstName} {lastName}
                </Typography>
                {isAdmin && <Typography variant="caption">(admin)</Typography>}
              </Grid>
              <Grid item xs={12}>
                {filteredNotificationTypes.map((t: NotificationType) => (
                  <FormGroup row key={`user-${username}-type-${t.type}`}>
                    {isEditing ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={this.updateSubscription(username, t.type)}
                            checked={isCheckedMap[username][t.type]}
                          />
                        }
                        label={t.description}
                      />
                    ) : isCheckedMap[username][t.type] ? (
                      <Grid
                        container
                        alignItems="flex-start"
                        alignContent="center"
                        spacing={1}
                        key={`user-${username}-type-${t.type}`}
                      >
                        <Grid item>
                          <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                        </Grid>
                        <Grid item>
                          <Typography variant="body2">{t.description}</Typography>
                        </Grid>
                      </Grid>
                    ) : null}
                  </FormGroup>
                ))}
                {!showOnlyRequiredNotificationTypes ? (
                  <FormGroup row>
                    {isEditing ? (
                      <StatusChangeNotificationSelect
                        emailOrUsername={username}
                        subscribedStatuses={this.state.statusChangeNotificationUsernames[username]}
                        usesPhoneInterview={usesPhoneInterview}
                        onAdd={this.handleSelectStatusSubscription({ username })}
                        onRemove={this.handleRemoveStatusSubscription({ username })}
                      />
                    ) : (
                      <StatusChangeChips
                        keyPrefix={username}
                        selectedStatuses={this.state.statusChangeNotificationUsernames[username]}
                      />
                    )}
                  </FormGroup>
                ) : null}
              </Grid>
              <hr />
            </Grid>
          ))}
        </Hidden>
      </>
    );
  }
}

export const JobNotificationSettings = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(JobNotificationSettingsComponent));
